import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  FiUpload, 
  FiImage, 
  FiX, 
  FiCheck,
  FiAlertCircle,
  FiFile
} from 'react-icons/fi'
import { useMedia } from '../../hooks/useMedia'
import { UploadProgress } from '../../types/media'

interface ImageUploadProps {
  onUploadComplete?: (urls: string[]) => void
  maxFiles?: number
  accept?: string[]
  folder?: string
}

const ImageUpload: React.FC<ImageUploadProps> = ({ 
  onUploadComplete, 
  maxFiles = 10,
  accept = ['image/*']
}) => {
  const { uploadFile } = useMedia()
  const [uploads, setUploads] = useState<UploadProgress[]>([])

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    // Initialize upload progress for all files
    const newUploads: UploadProgress[] = acceptedFiles.map(file => ({
      file,
      progress: 0,
      status: 'pending'
    }))

    setUploads(prev => [...prev, ...newUploads])

    // Upload files one by one
    const uploadedUrls: string[] = []
    
    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i]
      const uploadIndex = uploads.length + i

      try {
        // Update status to uploading
        setUploads(prev => prev.map((upload, index) => 
          index === uploadIndex 
            ? { ...upload, status: 'uploading', progress: 0 }
            : upload
        ))

        // Upload file
        const mediaFile = await uploadFile(file, (progress) => {
          setUploads(prev => prev.map((upload, index) => 
            index === uploadIndex 
              ? { ...upload, progress }
              : upload
          ))
        })

        if (mediaFile) {
          // Update status to completed
          setUploads(prev => prev.map((upload, index) => 
            index === uploadIndex 
              ? { ...upload, status: 'completed', progress: 100, mediaFile }
              : upload
          ))
          
          uploadedUrls.push(mediaFile.url)
        } else {
          throw new Error('Upload failed')
        }
      } catch (error) {
        // Update status to error
        setUploads(prev => prev.map((upload, index) => 
          index === uploadIndex 
            ? { ...upload, status: 'error', error: error instanceof Error ? error.message : 'Upload failed' }
            : upload
        ))
      }
    }

    // Callback with uploaded URLs
    if (onUploadComplete && uploadedUrls.length > 0) {
      onUploadComplete(uploadedUrls)
    }
  }, [uploadFile, uploads.length, onUploadComplete])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: accept.reduce((acc, type) => ({ ...acc, [type]: [] }), {}),
    maxFiles,
    maxSize: 10 * 1024 * 1024, // 10MB
  })

  const removeUpload = (index: number) => {
    setUploads(prev => prev.filter((_, i) => i !== index))
  }

  const clearCompleted = () => {
    setUploads(prev => prev.filter(upload => upload.status !== 'completed'))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="space-y-6">
      {/* Upload Area */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all duration-200 ${
          isDragActive
            ? 'border-primary bg-primary/5 scale-105'
            : 'border-neutral-300 hover:border-primary hover:bg-neutral-50'
        }`}
      >
        <input {...getInputProps()} />
        
        <motion.div
          animate={isDragActive ? { scale: 1.1 } : { scale: 1 }}
          transition={{ duration: 0.2 }}
        >
          <FiUpload size={48} className={`mx-auto mb-4 ${isDragActive ? 'text-primary' : 'text-neutral-400'}`} />
          
          {isDragActive ? (
            <div>
              <p className="text-lg font-semibold text-primary mb-2">Drop files here!</p>
              <p className="text-neutral-600">Release to upload your images</p>
            </div>
          ) : (
            <div>
              <p className="text-lg font-semibold text-neutral-800 mb-2">
                Drag & drop images here
              </p>
              <p className="text-neutral-600 mb-4">
                or <span className="text-primary font-medium">click to browse</span>
              </p>
              <div className="flex items-center justify-center space-x-4 text-sm text-neutral-500">
                <span>Max {maxFiles} files</span>
                <span>•</span>
                <span>Up to 10MB each</span>
                <span>•</span>
                <span>JPG, PNG, WebP</span>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Upload Progress */}
      <AnimatePresence>
        {uploads.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-neutral-200"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-neutral-800">
                Upload Progress ({uploads.length})
              </h3>
              
              {uploads.some(u => u.status === 'completed') && (
                <button
                  onClick={clearCompleted}
                  className="text-sm text-neutral-600 hover:text-neutral-800 transition-colors"
                >
                  Clear Completed
                </button>
              )}
            </div>

            <div className="space-y-3">
              {uploads.map((upload, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="flex items-center space-x-4 p-4 bg-neutral-50 rounded-xl"
                >
                  {/* File Icon */}
                  <div className={`p-2 rounded-lg ${
                    upload.status === 'completed' ? 'bg-green-100' :
                    upload.status === 'error' ? 'bg-red-100' :
                    'bg-neutral-200'
                  }`}>
                    {upload.status === 'completed' ? (
                      <FiCheck className="text-green-600" size={20} />
                    ) : upload.status === 'error' ? (
                      <FiAlertCircle className="text-red-600" size={20} />
                    ) : upload.file.type.startsWith('image/') ? (
                      <FiImage className="text-neutral-600" size={20} />
                    ) : (
                      <FiFile className="text-neutral-600" size={20} />
                    )}
                  </div>

                  {/* File Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-neutral-800 truncate">
                      {upload.file.name}
                    </p>
                    <div className="flex items-center space-x-3 text-sm text-neutral-600">
                      <span>{formatFileSize(upload.file.size)}</span>
                      {upload.status === 'error' && upload.error && (
                        <>
                          <span>•</span>
                          <span className="text-red-600">{upload.error}</span>
                        </>
                      )}
                      {upload.status === 'completed' && upload.mediaFile && (
                        <>
                          <span>•</span>
                          <span className="text-green-600">Uploaded successfully</span>
                        </>
                      )}
                    </div>

                    {/* Progress Bar */}
                    {(upload.status === 'uploading' || upload.status === 'processing') && (
                      <div className="mt-2">
                        <div className="w-full bg-neutral-200 rounded-full h-2">
                          <motion.div
                            className="bg-primary h-2 rounded-full"
                            initial={{ width: 0 }}
                            animate={{ width: `${upload.progress}%` }}
                            transition={{ duration: 0.3 }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Remove Button */}
                  <button
                    onClick={() => removeUpload(index)}
                    className="p-2 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <FiX size={16} />
                  </button>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ImageUpload
